import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getEmotionSummaryAPI,
  getFeatureCountAPI,
  getHwListAPI,
  getInteractionCountAPI,
  getInteractionHourlyStatAPI,
  getInteractionStatAPI,
  getPerformanceSummaryAPI,
  updateSubUserNickNameAPI,
} from '../api/picareDashboard';

const PICARE_MANAGER_ID = 'epnursing';

const toDateStr = (d) => d.toISOString().slice(0, 10);

const getDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 2);
  return { start: toDateStr(start), end: toDateStr(end) };
};

const initialState = {
  loading: {
    hwList: false,
    interactionCount: false,
    interactionStat: false,
    featureCount: false,
    hourlyStat: false,
    performanceSummary: false,
    emotionSummary: false,
  },
  managerId: PICARE_MANAGER_ID,
  hwId: '',
  hwList: [],
  interactionCount: [],
  interactionStat: [],
  featureCount: [],
  hourlyStat: [],
  performanceSummary: null,
  emotionSummary: [],
};

const name = 'picareDashboard';

export const fetchHwList = createAsyncThunk(
  `${name}/FETCH_HW_LIST`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { managerId } = getState().picareDashboard;
      const data = await getHwListAPI(managerId);
      return data ?? [];
    } catch (error) {
      return rejectWithValue([]);
    }
  },
);

export const fetchInteractionCount = createAsyncThunk(
  `${name}/FETCH_INTERACTION_COUNT`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { managerId } = getState().picareDashboard;
      const data = await getInteractionCountAPI(managerId);
      return data ?? [];
    } catch (error) {
      return rejectWithValue([]);
    }
  },
);

export const fetchInteractionStat = createAsyncThunk(
  `${name}/FETCH_INTERACTION_STAT`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { managerId } = getState().picareDashboard;
      const { start, end } = getDateRange();
      const data = await getInteractionStatAPI(managerId, start, end);
      return data ?? [];
    } catch (error) {
      return rejectWithValue([]);
    }
  },
);

export const fetchFeatureCount = createAsyncThunk(
  `${name}/FETCH_FEATURE_COUNT`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { managerId } = getState().picareDashboard;
      const { start, end } = getDateRange();
      const data = await getFeatureCountAPI(managerId, start, end);
      return data ?? [];
    } catch (error) {
      return rejectWithValue([]);
    }
  },
);

export const fetchHourlyStat = createAsyncThunk(
  `${name}/FETCH_HOURLY_STAT`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { managerId } = getState().picareDashboard;
      const data = await getInteractionHourlyStatAPI(managerId);
      return data ?? [];
    } catch (error) {
      return rejectWithValue([]);
    }
  },
);

export const fetchPerformanceSummary = createAsyncThunk(
  `${name}/FETCH_PERFORMANCE_SUMMARY`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { managerId } = getState().picareDashboard;
      const { start, end } = getDateRange();
      const data = await getPerformanceSummaryAPI(managerId, start, end);
      return data ?? null;
    } catch (error) {
      return rejectWithValue(null);
    }
  },
);

export const fetchEmotionSummary = createAsyncThunk(
  `${name}/FETCH_EMOTION_SUMMARY`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { managerId } = getState().picareDashboard;
      const data = await getEmotionSummaryAPI(managerId);
      return data ?? [];
    } catch (error) {
      return rejectWithValue([]);
    }
  },
);

export const updateSubUserNickName = createAsyncThunk(
  `${name}/UPDATE_SUB_USER_NICK_NAME`,
  async ({ userId, nickName }, { rejectWithValue }) => {
    try {
      await updateSubUserNickNameAPI(userId, nickName);
      return { userId, nickName };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchAllCharts = () => (dispatch) => {
  dispatch(fetchInteractionCount());
  dispatch(fetchInteractionStat());
  dispatch(fetchFeatureCount());
  dispatch(fetchHourlyStat());
  dispatch(fetchPerformanceSummary());
  dispatch(fetchEmotionSummary());
};

const loading = (key) => (state) => ({
  ...state,
  loading: { ...state.loading, [key]: true },
});

const done = (key, dataKey) => (state, action) => ({
  ...state,
  loading: { ...state.loading, [key]: false },
  [dataKey]: action.payload,
});

const fail = (key) => (state) => ({
  ...state,
  loading: { ...state.loading, [key]: false },
});

const picareDashboardSlice = createSlice({
  name,
  initialState,
  reducers: {
    setHwId: (state, action) => ({ ...state, hwId: action.payload }),
    setManagerId: (state, action) => ({ ...state, managerId: action.payload }),
  },
  extraReducers: {
    [fetchHwList.pending.type]: loading('hwList'),
    [fetchHwList.fulfilled.type]: done('hwList', 'hwList'),
    [fetchHwList.rejected.type]: fail('hwList'),

    [fetchInteractionCount.pending.type]: loading('interactionCount'),
    [fetchInteractionCount.fulfilled.type]: done('interactionCount', 'interactionCount'),
    [fetchInteractionCount.rejected.type]: fail('interactionCount'),

    [fetchInteractionStat.pending.type]: loading('interactionStat'),
    [fetchInteractionStat.fulfilled.type]: done('interactionStat', 'interactionStat'),
    [fetchInteractionStat.rejected.type]: fail('interactionStat'),

    [fetchFeatureCount.pending.type]: loading('featureCount'),
    [fetchFeatureCount.fulfilled.type]: done('featureCount', 'featureCount'),
    [fetchFeatureCount.rejected.type]: fail('featureCount'),

    [fetchHourlyStat.pending.type]: loading('hourlyStat'),
    [fetchHourlyStat.fulfilled.type]: done('hourlyStat', 'hourlyStat'),
    [fetchHourlyStat.rejected.type]: fail('hourlyStat'),

    [fetchPerformanceSummary.pending.type]: loading('performanceSummary'),
    [fetchPerformanceSummary.fulfilled.type]: done('performanceSummary', 'performanceSummary'),
    [fetchPerformanceSummary.rejected.type]: fail('performanceSummary'),

    [fetchEmotionSummary.pending.type]: loading('emotionSummary'),
    [fetchEmotionSummary.fulfilled.type]: done('emotionSummary', 'emotionSummary'),
    [fetchEmotionSummary.rejected.type]: fail('emotionSummary'),

    [updateSubUserNickName.fulfilled.type]: (state, action) => ({
      ...state,
      hwList: state.hwList.map((hw) => ({
        ...hw,
        subUsers: (hw.subUsers || []).map((su) =>
          su.userId === action.payload.userId
            ? { ...su, nickName: action.payload.nickName }
            : su,
        ),
      })),
    }),
  },
});

const { reducer: PicareDashboardReducer, actions } = picareDashboardSlice;
export const { setHwId, setManagerId } = actions;
export default PicareDashboardReducer;
